import { useEffect, useState } from "react";
import { mcpApi } from "../api/client";
import type { MCPCatalogEntry, MCPServer, MCPTool } from "../types";
import "./MCPView.css";
import { useToast } from "../lib/toast";

// Task 6.6 (U4/§9.3) — adding a server not in the catalog is a host-side
// text edit + a button click, never a form field here. This template is a
// copy/paste starting point for that file edit.
const CATALOG_ENTRY_TEMPLATE = `{
  "id": "my-server",
  "label": "My Server — one-line description",
  "runtime": "node",
  "package": "@scope/package-name@1.2.3",
  "args": [],
  "prefetch": false,
  "env_allow": [],
  "secret_refs": {},
  "tier": "confirm"
}`;

export default function MCPView() {
  const toast = useToast();
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [catalog, setCatalog] = useState<MCPCatalogEntry[]>([]);
  const [tools, setTools] = useState<Record<string, MCPTool[]>>({});
  const [showForm, setShowForm] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [starting, setStarting] = useState<Record<string, boolean>>({});
  const [form, setForm] = useState({
    name: "",
    server_type: "catalog" as "catalog" | "remote",
    catalog_id: "",
    url: "",
    env_vars: "",
    requires_confirmation: true,
  });

  const load = async () => {
    try {
      const [list, cat] = await Promise.all([mcpApi.list(), mcpApi.catalog()]);
      setServers(list);
      setCatalog(cat);
      if (!form.catalog_id && cat.length > 0) {
        setForm((f) => ({ ...f, catalog_id: cat.find((e) => e.enabled_by_default !== false)?.id || cat[0].id }));
      }
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadCatalog = async () => {
    setReloading(true);
    try {
      await mcpApi.reloadCatalog();
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setReloading(false);
    }
  };

  const create = async () => {
    try {
      const env = parseEnvVars(form.env_vars);
      await mcpApi.create({
        name: form.name,
        server_type: form.server_type,
        catalog_id: form.server_type === "catalog" ? form.catalog_id || undefined : undefined,
        url: form.server_type === "remote" ? form.url || undefined : undefined,
        env_vars: env,
        requires_confirmation: form.requires_confirmation,
      });
      setShowForm(false);
      setForm({
        name: "",
        server_type: "catalog",
        catalog_id: catalog[0]?.id || "",
        url: "",
        env_vars: "",
        requires_confirmation: true,
      });
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const start = async (id: string) => {
    setStarting((p) => ({ ...p, [id]: true }));
    try {
      await mcpApi.start(id);
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setStarting((p) => ({ ...p, [id]: false }));
    }
  };
  const stop = async (id: string) => {
    try {
      // Task 9.2 — `{stopped: false}` is a legitimate 200 response (Task
      // 6.4/M4's "the ACTUAL result, no more hardcoded {stopped: true}"),
      // not an exception; surface it as an error toast instead of silently
      // re-rendering with the server unchanged.
      const { stopped } = await mcpApi.stop(id);
      if (!stopped) toast.error(`Failed to stop server (it may not have been running).`);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  const remove = async (id: string) => {
    if (!confirm("Delete this server?")) return;
    await mcpApi.remove(id);
    load();
  };
  const viewTools = async (s: MCPServer) => {
    try {
      const t = await mcpApi.tools(s.id);
      setTools((prev) => ({ ...prev, [s.id]: t }));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const catalogLabel = (catalogId: string | null) =>
    catalog.find((e) => e.id === catalogId)?.label || catalogId || "—";

  return (
    <div className="view">
      <div className="view-header">
        <div>
          <h2 className="view-title">🔌 MCP Servers</h2>
          <p className="view-subtitle">Catalog servers (spawned by the mcp-runner sidecar) + remote HTTP/SSE</p>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn-sm" onClick={reloadCatalog} disabled={reloading}>
            {reloading ? "Reloading…" : "↻ Reload catalog"}
          </button>
          <button className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>
            {showForm ? "Cancel" : "+ Add Server"}
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: "var(--gap-lg)" }}>
        <div className="row-between">
          <div className="dim" style={{ fontSize: 12 }}>
            Catalog file: <code>backend/mcp_catalog.json</code> ({catalog.length} entries) — not adding a
            server you need? Edit that file on the host and click{" "}
            <strong>Reload catalog</strong>. No rebuild, no restart.
          </div>
          <button className="btn btn-sm" onClick={() => setShowTemplate((s) => !s)}>
            {showTemplate ? "Hide" : "Show"} new-entry template
          </button>
        </div>
        {showTemplate && (
          <pre className="mcp-template" style={{ marginTop: 10 }}>
            {CATALOG_ENTRY_TEMPLATE}
          </pre>
        )}
      </div>

      {showForm && (
        <div className="card fade-in" style={{ marginBottom: "var(--gap-lg)" }}>
          <div className="col">
            <div>
              <label className="form-label">Name</label>
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="form-label">Type</label>
              <select
                className="select"
                value={form.server_type}
                onChange={(e) => setForm({ ...form, server_type: e.target.value as "catalog" | "remote" })}
              >
                <option value="catalog">catalog (local, via mcp-runner)</option>
                <option value="remote">remote (HTTP/SSE)</option>
              </select>
            </div>
            {form.server_type === "catalog" ? (
              <div>
                <label className="form-label">Catalog entry</label>
                {/* No free-text command field — argv comes only from the
                    catalog, never from this form (§9.3: an "add server"
                    field that accepted argv would be prompt-injection RCE). */}
                <select
                  className="select"
                  value={form.catalog_id}
                  onChange={(e) => setForm({ ...form, catalog_id: e.target.value })}
                >
                  {catalog.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.label} ({entry.tier})
                    </option>
                  ))}
                </select>
                {catalog.length === 0 && (
                  <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>
                    No catalog entries loaded — try Reload catalog.
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className="form-label">URL (used as-is, full URL, https:// required)</label>
                <input
                  className="input"
                  placeholder="https://mcp.example.com/mcp"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                />
              </div>
            )}
            <div>
              <label className="form-label">Env vars / headers (optional, one KEY=value per line)</label>
              <textarea
                className="textarea"
                style={{ minHeight: 80, fontFamily: "var(--mono)" }}
                placeholder={form.server_type === "remote" ? "Authorization=Bearer abc123" : "GITHUB_TOKEN=abc123"}
                value={form.env_vars}
                onChange={(e) => setForm({ ...form, env_vars: e.target.value })}
              />
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                checked={form.requires_confirmation}
                onChange={(e) => setForm({ ...form, requires_confirmation: e.target.checked })}
              />
              <span>Require confirmation before each tool call</span>
            </label>
            <button className="btn btn-primary" onClick={create}>Add Server</button>
          </div>
        </div>
      )}

      {servers.length === 0 && <div className="empty-state"><h3>No MCP servers</h3></div>}

      <div className="col">
        {servers.map((s) => (
          <div key={s.id} className="card mcp-row">
            <div className="row-between" style={{ marginBottom: 8 }}>
              <div className="row" style={{ gap: 10 }}>
                <strong>{s.name}</strong>
                <span className="tag tag-muted">{s.server_type}</span>
                <span className={`tag ${statusTag(s)}`}>{s.running ? "running" : s.status}</span>
                {s.requires_confirmation !== false && (
                  <span className="tag tag-muted" title="Every tool call from this server suspends the turn for approval">
                    confirm-tier
                  </span>
                )}
              </div>
              <div className="row">
                {s.running ? (
                  <button className="btn btn-sm" onClick={() => stop(s.id)}>Stop</button>
                ) : (
                  <button className="btn btn-sm btn-primary" onClick={() => start(s.id)} disabled={!!starting[s.id]}>
                    {starting[s.id] ? "Starting…" : "Start"}
                  </button>
                )}
                <button className="btn btn-sm" onClick={() => viewTools(s)}>Tools</button>
                <button className="btn btn-danger btn-sm" onClick={() => remove(s.id)}>Delete</button>
              </div>
            </div>
            <div className="dim" style={{ fontSize: 12 }}>
              {s.server_type === "catalog" ? catalogLabel(s.catalog_id) : s.url}
            </div>
            {starting[s.id] && (
              <div className="dim" style={{ fontSize: 11 }}>
                First start of a non-prefetched entry can take up to ~60s (on-demand download).
              </div>
            )}
            {s.last_error && (
              <div className="mcp-error">{s.last_error}</div>
            )}
            {tools[s.id] && (
              <div className="mcp-tools">
                <div className="mcp-tools-title">Discovered tools ({tools[s.id].length})</div>
                {tools[s.id].length === 0 && <div className="dim">No tools exposed</div>}
                {tools[s.id].map((t) => (
                  <div key={t.name} className="mcp-tool">
                    <code>{t.name}</code>
                    {t.description && <div className="dim" style={{ fontSize: 11 }}>{t.description}</div>}
                    {t.inputSchema && Object.keys(t.inputSchema).length > 0 && (
                      <details className="mcp-tool-schema">
                        <summary>input schema</summary>
                        <pre>{JSON.stringify(t.inputSchema, null, 2)}</pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function statusTag(s: MCPServer): string {
  if (s.running) return "tag-success";
  if (s.status === "error") return "tag-danger";
  return "tag-muted";
}

/** Parse a textarea of "KEY=value" lines into a record. Empty/blank lines and
 * lines without `=` are ignored. Surrounding quotes on the value are stripped. */
function parseEnvVars(text: string): Record<string, string> | undefined {
  if (!text.trim()) return undefined;
  const out: Record<string, string> = {};
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    const idx = line.indexOf("=");
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    let val = line.slice(idx + 1).trim();
    if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
      val = val.slice(1, -1);
    }
    if (key) out[key] = val;
  }
  return Object.keys(out).length ? out : undefined;
}
